import {isApolloError} from '@apollo/client';
import isEqual from 'react-fast-compare';

import {AntibodyStateData, initializeAntibodyDataOnOpen} from 'antibodies/AntibodyStateActions';
import {ArchiveRecord} from 'archive/ArchiveConstants';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {
  InventoryFeature,
  RegistryFeature,
  ResultsFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import BaseFolderItem from 'folder-items/BaseFolderItem';
import {createEmbeddedDashboardTab} from 'insights/embeddings/backbone/InitializeBioentityEmbeddedDashboardTab';
import {GetBioentityAnalyticsDashboardQuery} from 'insights/embeddings/data/__generated/GetBioentityAnalyticsDashboardQuery';
import {fetchBioentityAnalyticsDashboardIfEnabled} from 'insights/embeddings/data/AnalyticsDashboardLinkActions';
import MetadataTabState from 'metadata/MetadataTabState';
import {
  ComplexPolymerStateData,
  fetchComplexPolymerData,
} from 'modbio/complex-polymers/tabs/ComplexPolymerStateActions';
import {AssemblyHistoryQuery} from 'molbio/visual-history/assembly-history/__generated/AssemblyHistoryQuery';
import {fetchAssemblyHistory} from 'molbio/visual-history/assembly-history/AssemblyHistoryActions';
import * as navigation from 'Navigation';
import {TabState} from 'Navigation';
import NoteStore from 'notebook/bioentities/NoteStore';
import NoteTabState from 'notebook/notes/tabs/NoteTabState';
import ChildEntityTabState from 'registry/child-entities/ChildEntityTabState';
import EntityMapTabState from 'registry/entity-map/tab/EntityMapTabState';
import RelevantItemsTabState from 'relevant-items/RelevantItemsTabState';
import EntityResultsTabState from 'results/linked-results/EntityResultsTabState';
import EntityTestOrdersTabState from 'sample-tests/ui/test-orders/EntityTestOrdersTabState';
import StorageTabState from 'samples/StorageTabState';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import {getActiveShareLinkItemId} from 'ShareAccess';
import EntityStore from 'stores/EntityStore';
import {asJQueryPromise} from 'util/Promises';
import {Optional} from 'util/Types';

import FileState from './FileState';

export interface FolderItemStateAttributes {
  antibodyData?: AntibodyStateData | null;
  assemblyHistoryData?: AssemblyHistoryQuery | null;
  bioentityDashboardData?: GetBioentityAnalyticsDashboardQuery;
  complexPolymerData?: ComplexPolymerStateData | null;
}

export class FolderItemStateModel extends BaseFolderItem {
  cleanUp?: () => void;
  duplicate?: () => void;
}

export default class FolderItemState extends FileState {
  static Model: typeof FolderItemStateModel;
  // FolderItemState and descendant classes (probably unintentionally) shadow
  // fileType() from FileState with a static property of the same name.
  // I suspect this is just a mixup from the lack of static typing, that persisted
  // through copy-pasting to make new classes.
  static fileType: string;

  override id!: string;
  hasNote!: boolean;
  noteTab!: NoteTabState;
  metadataTab!: TabState;
  resultsTab!: EntityResultsTabState;
  storageTab!: StorageTabState;
  childEntityTabs!: Array<TabState> | null;
  embeddedDashboardTab!: TabState | null;
  relevantItemsTab!: RelevantItemsTabState | null;
  testOrdersTab!: EntityTestOrdersTabState | null;
  entityMapTab!: EntityMapTabState | null;

  static initClass(): void {
    this.prototype.hasNote = true;
  }

  hasResults(): boolean {
    return (
      isFeatureEnabled({feature: ResultsFeature.RESULTS_SHOW_DETAILS, item: {id: this.id}}) &&
      EntityStore.currentUser()
    );
  }

  hasStorage(): boolean {
    return (
      isFeatureEnabled({feature: InventoryFeature.INVENTORY_DISCOVER}) &&
      Boolean(this.model().tagSchema()?.isContainable)
    );
  }

  hasMetadata(): boolean {
    return isFeatureEnabled({feature: RegistryFeature.REGISTRY_SHOW_DETAILS, item: {id: this.id}});
  }

  hasChildEntityTabs(): boolean {
    return (
      isFeatureEnabled({feature: RegistryFeature.REGISTRY_OPEN, item: {id: this.id}}) &&
      BenchlingConfig.REGISTRY_ENABLE_CHILD_ENTITIES
    );
  }

  override hasUnsavedChanges(): boolean {
    return this.hasNote && NoteStore.isUnsaved(this.id);
  }

  hasRelevantItemsTab(): boolean {
    return this.isFolderItem() && getActiveShareLinkItemId() == null;
  }

  hasTestOrders(): boolean {
    return this.isRegistryItem() && BenchlingConfig.SAMPLE_TESTS_ENABLE;
  }

  activateTestOrdersTab(): void {
    if (this.hasTestOrders()) {
      this.activateTab(EntityTestOrdersTabState);
    }
  }

  model(): FolderItemStateModel {
    return this.get(this.fileType());
  }

  override initialize(attributes: FolderItemStateAttributes, options: unknown): void {
    // TODO: Switch to rest params
    // oxlint-disable-next-line prefer-rest-params
    super.initialize(...arguments);
    this.set({
      buffers: new navigation.BufferStates([new navigation.BufferState(), new navigation.BufferState()]),
    });
    this.model().set({fileState: this});
    this.handleModelChanges();

    // Create note tab.
    // Even though we don't attach it for non-eln users, it loads the note into the store, which
    // is used by cloning/duplicate modal.
    if (this.hasNote) {
      this.noteTab = new NoteTabState({model: this.model()}, {forDescription: true});
    }
    if (this.hasMetadata()) {
      this.metadataTab = new MetadataTabState({model: this.model()});
      // TODO(BNCH-146401): Remove this flag check once Entity Map tab rollout is complete for all tenants.
      if (BenchlingConfig.REGISTRY_ENABLE_ENTITY_MAP_TAB) {
        this.entityMapTab = new EntityMapTabState({model: this.model()});
      }
    }
    if (this.hasResults()) {
      this.resultsTab = new EntityResultsTabState({model: this.model()});
    }
    if (this.hasStorage()) {
      this.storageTab = new StorageTabState({model: this.model()});
    }
    this.setChildEntityTabStates(this.model());

    if (attributes.bioentityDashboardData) {
      this.embeddedDashboardTab = createEmbeddedDashboardTab(attributes.bioentityDashboardData);
    }
    if (this.hasRelevantItemsTab()) {
      this.relevantItemsTab = new RelevantItemsTabState({model: this.model()});
    }
    if (this.hasTestOrders()) {
      this.testOrdersTab = new EntityTestOrdersTabState({modelId: this.model().id});
    }
  }

  override registerTitleAndURLSync(): void {
    this.on('change:name', () => {
      this.syncTitleAndURL();
    });
    this.listenTo(this.model(), 'change:editURL', () => {
      this.syncTitleAndURL();
    });
  }

  override beforeActivate(): void {
    this.model().setBanner();
  }

  cleanUp(): void {
    // TODO(josh) These cleanUp methods shouldn't be called to clean up React state - ideally we fire an
    // action and the Stores handle the action.
    this.buffers().forEach((buffer) => {
      buffer.tabs().forEach((tab) => {
        if (typeof tab.cleanUp === 'function') {
          tab.cleanUp();
        }
      });
    });
    __guardMethod__(this.model(), 'cleanUp', (o: FolderItemStateModel) => {
      o.cleanUp!();
    });
  }

  override isFolderItem(): boolean {
    return true;
  }

  isRegistryItem(): boolean {
    return false;
  }

  removeChildEntityTabs(): void {
    if (this.childEntityTabs != null) {
      this.childEntityTabs.forEach((tab) => {
        tab.close();
      });
      this.childEntityTabs = null;
    }
  }

  setChildEntityTabStates(model: FolderItemStateModel): void {
    if (this.hasChildEntityTabs()) {
      assert(this.childEntityTabs == null, 'Expected existing tabs to have been closed before setting');
      const tagSchema = model.tagSchema();
      const childEntitySchemaSummaries = tagSchema ? tagSchema.childEntitySchemaSummaries : [];
      this.childEntityTabs = childEntitySchemaSummaries
        .filter((childEntitySchemaSummary) => !isArchived(childEntitySchemaSummary)) //  .archiveRecord == null
        .map(
          (childEntitySchemaSummary) =>
            new ChildEntityTabState({
              model,
              childSchemaId: childEntitySchemaSummary.id,
              title: childEntitySchemaSummary.name,
            })
        );
    }
  }

  addChildEntityTabs(): void {
    if (this.childEntityTabs != null) {
      for (const childEntityTab of this.childEntityTabs) {
        this.buffers().addTab(childEntityTab, {side: 'right', active: false});
      }
    }
  }

  addRelevantItemTab(): void {
    if (this.relevantItemsTab != null) {
      this.buffers().addTab(this.relevantItemsTab, {side: 'right', active: false});
    }
  }

  addTestOrdersTab(): void {
    if (this.testOrdersTab != null) {
      this.buffers().addTab(this.testOrdersTab, {side: 'right', active: false});
    }
  }

  resetChildEntityTabs(model: FolderItemStateModel): void {
    this.removeChildEntityTabs();
    this.setChildEntityTabStates(model);
    this.addChildEntityTabs();
  }

  handleModelChanges(): void {
    const model = this.model();
    this.set({name: model.get('name')});
    this.listenTo(model, 'change:name', () => this.set({name: model.get('name')}));

    this.set({tagSchema: model.get('tagSchema')});
    this.listenTo(model, 'change:tagSchema', () => this.set({tagSchema: model.get('tagSchema')}));
    this.listenTo(model, 'change:tagSchema', () => {
      const oldChildEntitySummaries = model.previous('tagSchema')?.childEntitySchemaSummaries;
      const newChildEntitySummaries = model.get('tagSchema')?.childEntitySchemaSummaries;
      if (!isEqual(oldChildEntitySummaries, newChildEntitySummaries)) {
        this.resetChildEntityTabs(model);
      }
    });
  }

  override name(): string | null | undefined {
    if (this.isRegistryItem()) {
      // See benchling/registry/mixins/registry_item_mixin.py::display_label
      return this.model().displayLabel();
    }
    return this.model().name();
  }

  override archiveRecord(): ArchiveRecord | null {
    return this.model().archiveRecord();
  }

  override setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    this.model().setArchiveRecord(archiveRecord);
  }

  override permissions(): Permissions {
    return this.model().get('permissions');
  }

  override setPermissions(permissions: Permissions): void {
    this.model().setPermissions(permissions);
  }

  override schemaValidationState(): Optional<SchemaValidationState> {
    return this.model().schemaValidationState();
  }

  static openFile(fileId: string, param?: {versionId?: number}): JQueryPromise<FileState> {
    if (param == null) {
      param = {};
    }
    const {versionId} = param;
    const [ThisFileState, Model] = [this, this.Model];
    // Keep in sync with client/test/lib/eln/makeEntry.ts
    const model = new Model({id: fileId});

    // If loading a bioentity that has an embedded Insights dashboard, we'd like to render the dashboard tab
    // at the same time as the rest of the tabs. The below call to Promise.all calls the main tag schema data
    // fetch and the linked dashboard query in parallel, ensuring that the file is not initialized until we
    // know whether or not there should be an embedded dashboard tab. The check is only run for bioentities,
    // not for entries. As Insights dashboards are queried using GraphQL, the requests need to be separate -
    // though ideally, eventually the two requests are unified into a single GraphQL request.

    return asJQueryPromise(
      Promise.all([
        // @ts-expect-error Backbone.Model.fetch doesn't include a type for versionId
        model.fetch({data: {view: true}, versionId}),
        fetchBioentityAnalyticsDashboardIfEnabled(fileId),
        fetchAssemblyHistory(fileId).catch((e) => {
          // Continue opening sequence workspace even if assembly history query fails.
          if (isApolloError(e)) {
            return null;
          }
          throw e;
        }),
        fetchComplexPolymerData(fileId),
        initializeAntibodyDataOnOpen(fileId),
      ])
    )
      .fail((response) => {
        if (typeof model.cleanUp === 'function') {
          model.cleanUp();
        } // model may have registered listeners in its constructor (see ProxyFile)
      })
      .then(
        // @ts-expect-error TODO: JQueryPromises can pass null to `then`, figure out how to handle safely.
        ([modelResult, bioentityDashboardData, assemblyHistoryData, complexPolymerData, antibodyData]) => {
          const attrs: {[k: string]: unknown} = {
            id: fileId,
            bioentityDashboardData,
            assemblyHistoryData,
            complexPolymerData,
            antibodyData,
          };
          attrs[this.fileType] = model;

          return new ThisFileState(attrs, {parse: true});
        }
      );
  }
}
FolderItemState.initClass();

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
function __guardMethod__<T extends {[f: string]: any}>(
  obj: T,
  methodName: string,
  transform: (object: T, method: string) => unknown
): unknown {
  if (obj != null && typeof obj[methodName] === 'function') {
    return transform(obj, methodName);
  } else {
    return undefined;
  }
}
